import { useEffect, useMemo, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Link } from 'react-router'
import { History } from 'lucide-react'
import { Alert, Button, Drawer, Field, HelpTip, Input, PageHeader, PageLayout, Select, SegmentedControl, StatePanel, StatusBadge, StatusBar } from '@/components/common'
import { useOrganization } from '@/contexts/OrganizationContext'
import { PRODUCT_KEYS } from '@/config/studioProducts'
import { DeviceStage, NativePreview } from './NativePreview'
import { THEMES, themeKeyFor, visiblePreviewComponents } from './nativeSportsbookManifest'
import { useNativeSportsbookTheme } from './useNativeSportsbookTheme'
import workspace from './nativeWorkspace.module.css'
import styles from './nativeSportsbookTheme.module.css'

const UPPERCASE_HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/

const DEVICES = [{ value: 'ios', label: 'iPhone' }, { value: 'android', label: 'Android' }]
const APPEARANCE_OPTIONS = [{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]

function AccentField({ label, colorLabel, hexLabel, value, contrast, disabled, onChange }) {
  const [text, setText] = useState(value)
  useEffect(() => { setText(value) }, [value])
  const passes = contrast >= 4.5
  return (
    <Field label={label}>
      <div className={styles.accentControl}>
        <input
          type="color"
          aria-label={colorLabel}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
        />
        <Input
          size="compact"
          aria-label={hexLabel}
          pattern="^#[0-9A-Fa-f]{6}$"
          value={text}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value.toUpperCase()
            setText(next)
            if (UPPERCASE_HEX_COLOR_PATTERN.test(next)) onChange(next)
          }}
        />
        <span className={styles.contrastReadout}>
          {`Contrast ${Number.isFinite(contrast) ? contrast.toFixed(1) : '—'}:1`}
          <StatusBadge size="small" type={passes ? 'success' : 'warning'}>{passes ? 'Passes' : 'Below 4.5:1'}</StatusBadge>
        </span>
        <HelpTip text={`${label.startsWith('Highlight') ? 'Highlight' : 'Accent'} must contrast at least 4.5:1 against the scheme background.`} label={`${label} contrast help`} />
      </div>
    </Field>
  )
}

function ThemeHistoryDrawer({ open, onClose, revisions, onSelect }) {
  return (
    <Drawer open={open} side="right" title="Theme history" onClose={onClose}>
      {revisions.length ? (
        <ul className={styles.historyList}>
          {revisions.map((revision, index) => (
            <li key={revision.id}>
              <button type="button" className={styles.historyRow} onClick={() => onSelect(revision)}>
                <span>{`r${revision.revision}`}</span>
                <code>{revision.content_sha256.slice(0, 12)}</code>
                <time dateTime={revision.created_at}>{new Date(revision.created_at).toLocaleString()}</time>
                {index === 0 ? <StatusBadge size="small" type="success">Current</StatusBadge> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <StatePanel state="empty" title="No history yet" description="Save the brand theme to start its revision history." compact />
      )}
    </Drawer>
  )
}

// Draft, dirty flag, and selected revision belong to one organization; keying
// the workspace by slug discards them on an organization switch instead of
// letting a retained draft save into the newly selected organization.
export default function NativeSportsbookThemePage() {
  const { currentOrg } = useOrganization()
  return <ThemeWorkspace key={currentOrg?.slug ?? ''} slug={currentOrg?.slug} />
}

function ThemeWorkspace({ slug }) {
  const { hasProductAccess } = useOrganization()
  const theme = useNativeSportsbookTheme(slug)
  const [device, setDevice] = useState('ios')
  const [historyOpen, setHistoryOpen] = useState(false)
  const canConfigure = hasProductAccess(PRODUCT_KEYS.NATIVE_SPORTSBOOK, 'configure')
  const homeComponents = theme.homeDocument.screens[0].components
  const visibleIds = useMemo(
    () => new Set(visiblePreviewComponents(theme.homeDocument, 'US-NJ', 'anonymous').map((component) => component.id)),
    [theme.homeDocument],
  )
  const requestError = theme.saveMutation.error

  return (
    <DndProvider backend={HTML5Backend}>
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="Theme" compact />
        {theme.themeQuery.isError ? (
          <StatePanel
            state="error"
            title="Theme workspace failed"
            description={theme.themeQuery.error?.response?.data?.error || theme.themeQuery.error?.message}
            onRetry={() => theme.themeQuery.refetch()}
            compact
          />
        ) : null}
        {requestError ? <Alert variant="error">{requestError?.response?.data?.error || requestError.message}</Alert> : null}
        <div className={styles.workspace}>
          <div className={styles.canvasColumn}>
            <div className={workspace.canvasStrip}>
              <SegmentedControl label="Preview device" items={DEVICES} value={device} onValueChange={setDevice} />
            </div>
            <div className={styles.canvasArea}>
              <DeviceStage>
                <NativePreview
                  key={device}
                  manifest={{ ...theme.homeDocument, theme: theme.draft }}
                  components={homeComponents}
                  visibleIds={visibleIds}
                  device={device}
                  sessionState="anonymous"
                  interactive={false}
                  slug={slug}
                />
              </DeviceStage>
            </div>
          </div>
          <section aria-label="Theme settings" className={styles.inspector}>
            <h2 className={styles.inspectorHeading}>Brand theme</h2>
            <Field label="Preset">
              <Select aria-label="Preset" value={themeKeyFor({ theme: theme.draft })} disabled={!canConfigure} onChange={(event) => theme.selectPreset(event.target.value)}>
                {Object.entries(THEMES).map(([key, preset]) => <option key={key} value={key}>{preset.label}</option>)}
              </Select>
            </Field>
            <Field label="Default appearance">
              <SegmentedControl
                label="Default appearance"
                items={APPEARANCE_OPTIONS.map((option) => ({ ...option, disabled: !canConfigure }))}
                value={theme.draft.appearance.default}
                onValueChange={theme.setDefaultAppearance}
              />
            </Field>
            <AccentField
              label="Accent · dark"
              colorLabel="Dark accent"
              hexLabel="Dark accent hex"
              value={theme.draft.colors.accent}
              contrast={theme.contrast.dark}
              disabled={!canConfigure}
              onChange={(hex) => theme.setAccent('dark', hex)}
            />
            <AccentField
              label="Accent · light"
              colorLabel="Light accent"
              hexLabel="Light accent hex"
              value={theme.draft.appearance.schemes.light.accent}
              contrast={theme.contrast.light}
              disabled={!canConfigure}
              onChange={(hex) => theme.setAccent('light', hex)}
            />
            <AccentField
              label="Highlight · dark"
              colorLabel="Dark highlight"
              hexLabel="Dark highlight hex"
              value={theme.draft.colors.highlight}
              contrast={theme.contrast.highlightDark}
              disabled={!canConfigure}
              onChange={(hex) => theme.setHighlight('dark', hex)}
            />
            <AccentField
              label="Highlight · light"
              colorLabel="Light highlight"
              hexLabel="Light highlight hex"
              value={theme.draft.appearance.schemes.light.highlight}
              contrast={theme.contrast.highlightLight}
              disabled={!canConfigure}
              onChange={(hex) => theme.setHighlight('light', hex)}
            />
            <div className={styles.usageBlock}>
              <h3 className={styles.inspectorHeading}>Where it&rsquo;s used</h3>
              {theme.usage.length ? (
                <ul className={styles.usage}>
                  {theme.usage.map((row) => (
                    <li key={row.revision} className={styles.usageRow}>
                      <span>{`Home r${row.revision}`}</span>
                      <span>{row.theme_revision != null ? `theme r${row.theme_revision}` : 'no theme snapshot'}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.usageEmpty}>No Home revisions yet.</p>
              )}
              {theme.homeOutdated ? (
                <p className={styles.outdatedNote}>
                  {`Home r${theme.usage[0]?.revision} uses theme r${theme.usage[0]?.theme_revision ?? 0}. Save a new Home revision to ship theme r${theme.current?.revision}.`}
                  <Link to="/dashboard/native-sportsbook/manifests">Open Home screen</Link>
                </p>
              ) : null}
            </div>
          </section>
        </div>
        <StatusBar
          size="regular"
          label="Theme status"
          start={(
            <div className={workspace.identity}>
              <strong>{theme.current ? `Theme r${(theme.selectedRevision ?? theme.current).revision}` : 'No theme yet'}</strong>
              <StatusBadge size="small" type={theme.dirty ? 'warning' : 'success'}>{theme.dirty ? 'Unsaved' : 'Saved'}</StatusBadge>
            </div>
          )}
          message={{ text: theme.notice, tone: theme.noticeTone }}
          end={(
            <div className={workspace.barActions}>
              <Button variant="secondary" size="compact" onClick={() => setHistoryOpen(true)}><History size={14} aria-hidden="true" />History</Button>
              <Button
                size="compact"
                variant={theme.dirty ? 'primary' : 'secondary'}
                disabled={!canConfigure || !theme.canSave || theme.saveMutation.isPending}
                loading={theme.saveMutation.isPending}
                onClick={() => theme.saveMutation.mutate()}
              >
                Save
              </Button>
            </div>
          )}
        />
      </PageLayout>
      <ThemeHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        revisions={theme.revisions}
        onSelect={(revision) => { theme.loadRevision(revision); setHistoryOpen(false) }}
      />
    </DndProvider>
  )
}
